import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

function BlockRenderer({ block }) {
  const t = block?.type;

  if (t === "heading") {
    const cls = {
      1: "font-serif text-4xl sm:text-5xl md:text-6xl text-zinc-950 tracking-tight leading-[1.05] mb-4",
      2: "font-serif text-3xl sm:text-4xl text-zinc-950 tracking-tight leading-tight mb-3",
      3: "font-serif text-2xl sm:text-3xl text-zinc-950 tracking-tight leading-tight mb-2",
    }[block.level] || "font-serif text-3xl";
    const Tag = `h${block.level}`;
    return React.createElement(Tag, { className: cls }, block.text || "");
  }

  if (t === "paragraph") {
    return (
      <p className="text-base sm:text-lg text-zinc-800 leading-relaxed whitespace-pre-wrap">
        {block.text || ""}
      </p>
    );
  }

  if (t === "image") {
    if (!block.url) return null;
    const src = block.url.startsWith("http") ? block.url : `${process.env.REACT_APP_BACKEND_URL}${block.url}`;
    return (
      <figure className="my-2">
        <img src={src} alt={block.alt || ""} className="w-full h-auto rounded-2xl border border-zinc-200 object-cover" loading="lazy" />
        {block.caption && (
          <figcaption className="mt-2 text-xs text-zinc-500 italic text-center">{block.caption}</figcaption>
        )}
      </figure>
    );
  }

  if (t === "button") {
    const isExternal = /^https?:\/\//i.test(block.href || "");
    const cls = block.style === "outline"
      ? "inline-block px-6 py-2.5 rounded-full border border-zinc-950 text-zinc-950 hover:bg-zinc-950 hover:text-white transition-colors font-medium text-sm"
      : "btn-gold text-sm inline-block";
    if (isExternal) {
      return <a href={block.href || "#"} className={cls} target="_blank" rel="noreferrer">{block.label || "Button"}</a>;
    }
    return <Link to={block.href || "/"} className={cls}>{block.label || "Button"}</Link>;
  }

  if (t === "columns") {
    const grid = block.columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
    return (
      <div className={`grid grid-cols-1 ${grid} gap-6`}>
        {(block.cols || []).map((c, i) => (
          <div key={`col-${i}-${(c.text || "").slice(0,10)}`} className="text-base text-zinc-800 leading-relaxed whitespace-pre-wrap">
            {c.text || ""}
          </div>
        ))}
      </div>
    );
  }

  if (t === "spacer") {
    return <div style={{ height: `${block.height || 40}px` }} aria-hidden="true" />;
  }

  if (t === "divider") {
    return <hr className="border-t border-zinc-200 my-2" />;
  }

  return null;
}

export default function CustomPage() {
  const { slug } = useParams();
  const { data: page, isLoading, isError, error } = useQuery({
    queryKey: ["public-page", slug],
    queryFn: async () => (await api.get(`/pages/${slug}`)).data,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="custom-page-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading page…
      </div>
    );
  }

  if (isError || !page) {
    const notFound = error?.response?.status === 404;
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="custom-page-not-found">
        <div className="overline mb-4">404</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-zinc-950 tracking-tight mb-4">
          {notFound ? "Page not found" : "Terjadi kesalahan"}
        </h1>
        <p className="text-zinc-600 mb-8">
          {notFound ? "The page you requested may have been removed or has not been published." : "Coba muat ulang beberapa saat lagi."}
        </p>
        <Link to="/" className="btn-gold text-sm inline-block">Back to Home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20" data-testid={`custom-page-${page.slug}`}>
      <div className="overline mb-4">{page.slug}</div>
      <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-zinc-950 tracking-tight leading-[1.05] mb-10" data-testid="custom-page-title">
        {page.title}
      </h1>
      <div className="space-y-6" data-testid="custom-page-blocks">
        {(page.blocks || []).map((b, i) => (
          <BlockRenderer key={b.id || i} block={b} />
        ))}
      </div>
      {(!page.blocks || page.blocks.length === 0) && (
        <p className="text-zinc-500 italic text-center py-12">Halaman ini masih kosong.</p>
      )}
    </div>
  );
}
